/* src/components/testing/countingSpan/CountingSpanInstructions.js */
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import StimulusDisplay from './StimulusDisplay';
import RecallInterface from './RecallInterface';
import { COUNTING_SPAN_DEFAULTS } from '../../../constants/cstConstants';

// --- Local Utilities (Minimal & Non-Sensitive) ---

// Simple layout function JUST for placing pre-defined practice items.
// No sophisticated collision detection needed for fixed examples.
const simplePracticeLayout = (itemsToPlace) => {
    const placedItems = [];
    const width = 450; // Smaller area within the StimulusDisplay SVG
    const height = 300;
    const padding = 20;

    itemsToPlace.forEach((item, index) => {
        placedItems.push({
            ...item,
            x: Math.random() * (width - 2 * padding) + padding + 25, // Offset within 500x350 viewbox
            y: Math.random() * (height - 2 * padding) + padding + 25,
            id: `p_item_${Date.now()}_${index}` // Simple unique key
        });
    });
    return placedItems;
};

// Local scoring function (simple comparison) - kept for practice feedback.
const calculatePracticeScore = (presentedCounts, recalledCounts) => {
    let score = 0;
    const setSize = Array.isArray(presentedCounts) ? presentedCounts.length : 0;
    for (let i = 0; i < setSize; i++) {
        if (recalledCounts && typeof recalledCounts[i] === 'number' && recalledCounts[i] === presentedCounts[i]) {
            score++;
        }
    }
    return score;
};

// --- Fixed Practice Examples Data ---

const PROCESSING_PRACTICE_EXAMPLES = [
    { targetCount: 5, items: simplePracticeLayout([ {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'greenCircle'}, {type: 'blueSquare'}, {type: 'greenCircle'} ]) },
    { targetCount: 8, items: simplePracticeLayout([ {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'greenCircle'}, {type: 'blueSquare'}, {type: 'blueSquare'}, {type: 'greenCircle'} ]) },
    { targetCount: 4, items: simplePracticeLayout([ {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'greenCircle'}, {type: 'greenCircle'}, {type: 'greenCircle'}, {type: 'blueSquare'} ]) },
];

const RECALL_PRACTICE_EXAMPLE_TRIALS = [
    { targetCount: 6, items: simplePracticeLayout([ {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'greenCircle'}, {type: 'blueSquare'} ]) },
    { targetCount: 3, items: simplePracticeLayout([ {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'greenCircle'}, {type: 'greenCircle'}, {type: 'blueSquare'} ]) },
    { targetCount: 7, items: simplePracticeLayout([ {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueSquare'}, {type: 'greenCircle'}, {type: 'blueSquare'} ]) },
    { targetCount: 5, items: simplePracticeLayout([ {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'greenCircle'}, {type: 'greenCircle'}, {type: 'blueSquare'} ]) },
];
// Extract the counts sequence for recall scoring
const RECALL_PRACTICE_CORRECT_SEQUENCE = RECALL_PRACTICE_EXAMPLE_TRIALS.map(t => t.targetCount); // [6, 3, 7, 5]
const RECALL_PRACTICE_SET_SIZE = RECALL_PRACTICE_EXAMPLE_TRIALS.length; // Should be 4

const INTEGRATED_PRACTICE_EXAMPLE_SET_TRIALS = [
     { targetCount: 4, items: simplePracticeLayout([ {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'greenCircle'}, {type: 'blueSquare'}, {type: 'greenCircle'} ]) },
     { targetCount: 9, items: simplePracticeLayout([ {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'greenCircle'}, {type: 'blueSquare'}, {type: 'blueSquare'} ]) },
     { targetCount: 6, items: simplePracticeLayout([ {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'blueCircle'}, {type: 'greenCircle'}, {type: 'blueSquare'} ]) },
];
const INTEGRATED_PRACTICE_CORRECT_SEQUENCE = INTEGRATED_PRACTICE_EXAMPLE_SET_TRIALS.map(t => t.targetCount); // [4, 9, 6]
const INTEGRATED_PRACTICE_SET_SIZE = INTEGRATED_PRACTICE_EXAMPLE_SET_TRIALS.length; // Should be 3
const INTEGRATED_PRACTICE_SETS = 1; // Only one set defined currently

// --- Component Definition ---

const CountingSpanInstructions = ({ onStartAssessment }) => {
    // --- State Management (Simplified for fixed examples) ---
    const [step, setStep] = useState('intro1');
    const [practiceSubStep, setPracticeSubStep] = useState('stimulus'); // 'stimulus', 'judgment', 'feedback', 'recall'
    const [trialNum, setTrialNum] = useState(0); // Index within the current practice step's examples
    // Removed setNum as we only have one integrated set defined
    const [currentStimulus, setCurrentStimulus] = useState(null); // { items: [...], targetCount: X }
    const [currentCorrectCount, setCurrentCorrectCount] = useState(null);
    // State for recall practice
    const [recallPracticeJudgments, setRecallPracticeJudgments] = useState([]); // Store judgment correctness
    const [practiceRecallSequence, setPracticeRecallSequence] = useState(Array(RECALL_PRACTICE_SET_SIZE).fill(null));
    // State for integrated practice
    const [integratedJudgmentsForSet, setIntegratedJudgmentsForSet] = useState([]);
    const [integratedRecallSequence, setIntegratedRecallSequence] = useState(Array(INTEGRATED_PRACTICE_SET_SIZE).fill(null));
    const [feedback, setFeedback] = useState('');

    // --- Setup Functions for Practice Steps ---
    const setupProcessingPractice = useCallback(() => {
        setTrialNum(0);
        setFeedback('');
        const example = PROCESSING_PRACTICE_EXAMPLES[0];
        setCurrentStimulus(example);
        setCurrentCorrectCount(example.targetCount);
        setPracticeSubStep('judgment');
    }, []);

    const setupRecallPractice = useCallback(() => {
        setTrialNum(0);
        setFeedback('');
        setRecallPracticeJudgments([]); // Clear judgments from previous attempt
        setPracticeRecallSequence(Array(RECALL_PRACTICE_SET_SIZE).fill(null));
        const example = RECALL_PRACTICE_EXAMPLE_TRIALS[0];
        setCurrentStimulus(example);
        setCurrentCorrectCount(example.targetCount);
        setPracticeSubStep('judgment');
    }, []);

    const setupIntegratedPracticeSet = useCallback(() => {
        setTrialNum(0);
        setFeedback('');
        setIntegratedJudgmentsForSet([]); // Clear judgments
        setIntegratedRecallSequence(Array(INTEGRATED_PRACTICE_SET_SIZE).fill(null));
        const example = INTEGRATED_PRACTICE_EXAMPLE_SET_TRIALS[0];
        setCurrentStimulus(example);
        setCurrentCorrectCount(example.targetCount);
        setPracticeSubStep('judgment');
    }, []);

    // --- Advancement Logic ---
    const handleNextOverallStep = useCallback(() => {
        setFeedback('');
        switch (step) {
            case 'intro1': setStep('intro2'); break;
            case 'intro2':
                setStep('processingPractice');
                setupProcessingPractice();
                break;
            case 'processingPractice_done':
                setStep('recallPractice');
                setupRecallPractice();
                break;
            case 'recallPractice_done':
                setStep('integratedPractice');
                // setSetNum(0); // Not needed if only 1 set
                setupIntegratedPracticeSet();
                break;
            case 'integratedPractice_done':
                if (onStartAssessment) onStartAssessment();
                break;
            default: setStep('intro1');
        }
    }, [step, onStartAssessment, setupProcessingPractice, setupRecallPractice, setupIntegratedPracticeSet]);

    // --- Handlers for User Actions within Practice Steps ---
    const handlePracticeJudgment = useCallback((isOdd) => {
        const isCorrect = (currentCorrectCount % 2 !== 0) === isOdd;
        let nextSubStep = 'feedback';
        let moreTrials = false;
        let nextMajorStep = null;
        let nextExampleIndex = trialNum + 1;

        setFeedback(`Trial ${trialNum + 1}: Judgment ${isCorrect ? 'Correct!' : 'Incorrect.'} (Count was ${currentCorrectCount})`);
        setPracticeSubStep('feedback');

        if (step === 'processingPractice') {
            moreTrials = nextExampleIndex < PROCESSING_PRACTICE_EXAMPLES.length;
            if (!moreTrials) nextMajorStep = 'processingPractice_done';
        } else if (step === 'recallPractice') {
            setRecallPracticeJudgments(prev => [...prev, { correct: isCorrect }]); // Store judgment result
            moreTrials = nextExampleIndex < RECALL_PRACTICE_EXAMPLE_TRIALS.length;
            if (!moreTrials) nextSubStep = 'recall'; // Move to recall after last item shown
        } else if (step === 'integratedPractice') {
            setIntegratedJudgmentsForSet(prev => [...prev, { correct: isCorrect }]); // Store judgment result
            moreTrials = nextExampleIndex < INTEGRATED_PRACTICE_EXAMPLE_SET_TRIALS.length;
            if (!moreTrials) nextSubStep = 'recall'; // Move to recall after last item shown
        }

        setTimeout(() => {
            setFeedback('');
            if (moreTrials) {
                setTrialNum(nextExampleIndex); // Move to next trial index
                let nextExample = null;
                if (step === 'processingPractice') nextExample = PROCESSING_PRACTICE_EXAMPLES[nextExampleIndex];
                else if (step === 'recallPractice') nextExample = RECALL_PRACTICE_EXAMPLE_TRIALS[nextExampleIndex];
                else if (step === 'integratedPractice') nextExample = INTEGRATED_PRACTICE_EXAMPLE_SET_TRIALS[nextExampleIndex];

                if (nextExample) {
                    setCurrentStimulus(nextExample);
                    setCurrentCorrectCount(nextExample.targetCount);
                    setPracticeSubStep('judgment'); // Show next stimulus/judgment prompt
                } else {
                     console.error("Error getting next practice example!"); // Should not happen if moreTrials is true
                }
            } else {
                // No more trials in this step/set
                if (nextMajorStep) {
                    setStep(nextMajorStep); // e.g., 'processingPractice_done'
                } else {
                    setPracticeSubStep(nextSubStep); // e.g., 'recall'
                }
            }
        }, 1000); // Feedback display time

    }, [step, practiceSubStep, trialNum, currentCorrectCount]); // Dependencies updated

    // --- Recall Practice Handlers ---
    const handlePracticeRecallChange = useCallback((newSequence) => {
        setPracticeRecallSequence(newSequence);
    }, []);

    const handleRecallPracticeSubmit = useCallback(() => {
        const score = calculatePracticeScore(RECALL_PRACTICE_CORRECT_SEQUENCE, practiceRecallSequence);
        const processingCorrect = recallPracticeJudgments.filter(j => j.correct).length;
        const processingTotal = recallPracticeJudgments.length;
        setFeedback(`Recall Practice Complete: Score ${score}/${RECALL_PRACTICE_SET_SIZE}. Judgments: ${processingCorrect}/${processingTotal}. (Correct sequence: ${RECALL_PRACTICE_CORRECT_SEQUENCE.join(', ')})`);
        setStep('recallPractice_done');
    }, [practiceRecallSequence, recallPracticeJudgments]); // Dependencies updated

    // --- Integrated Practice Handlers ---
    const handleIntegratedRecallChange = useCallback((newSequence) => {
        setIntegratedRecallSequence(newSequence);
    }, []);

    const handleIntegratedRecallSubmit = useCallback(() => {
        const score = calculatePracticeScore(INTEGRATED_PRACTICE_CORRECT_SEQUENCE, integratedRecallSequence);
        const processingCorrect = integratedJudgmentsForSet.filter(j => j.correct).length;
        const processingTotal = integratedJudgmentsForSet.length;
        // Using setNum+1 in feedback even though we only have 1 set defined
        setFeedback(`Integrated Practice Set 1 Feedback: Score ${score}/${INTEGRATED_PRACTICE_SET_SIZE}. Judgments: ${processingCorrect}/${processingTotal}. (Recalled: ${integratedRecallSequence.map(n => n ?? '?').join(', ')})`);
        setPracticeSubStep('feedback');

        setTimeout(() => {
            // Logic assumes only 1 integrated set for now
             setStep('integratedPractice_done');
        }, 1500);

    }, [integratedRecallSequence, integratedJudgmentsForSet]); // Dependencies updated

    // --- Render Logic ---
    const renderContent = () => {
        const textStyle = "text-left space-y-3 text-sm sm:text-base text-gray-700 mb-6";
        const strongStyle = "font-semibold text-gray-800";
        const highlightStyle = "font-semibold text-blue-600";
        const judgmentWindowSeconds = (COUNTING_SPAN_DEFAULTS.STIMULUS_JUDGMENT_WINDOW_MS || 4500) / 1000;

        // --- Render Introduction Steps ---
        if (step === 'intro1') {
            return ( <> <h2 className="text-xl sm:text-2xl font-semibold mb-4 text-gray-800">Counting Span Task</h2> <div className={textStyle}> <p>Welcome! This task measures working memory.</p> <p>You will see screens with <strong className={highlightStyle}>BLUE CIRCLES</strong>, green circles, and blue squares.</p> <p><strong>1. Count:</strong> On each screen, count the <strong className={highlightStyle}>BLUE CIRCLES</strong>.</p> <p><strong>2. Judge:</strong> Decide if your count is <strong className={strongStyle}>Odd or Even</strong> and click the corresponding button.</p> <p><strong className="text-orange-600">Important:</strong> You have a limited time ({judgmentWindowSeconds} seconds) for both counting and judging each screen.</p> </div> <button onClick={handleNextOverallStep} className="px-5 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm shadow-sm transition-colors">Next</button> </> );
        }
        if (step === 'intro2') {
             return ( <> <h2 className="text-xl sm:text-2xl font-semibold mb-4 text-gray-800">Remembering the Counts</h2> <div className={textStyle}> <p><strong>3. Remember:</strong> While counting and judging, you MUST also <strong className={strongStyle}>remember each blue circle count</strong> in the exact order it appeared.</p> <p>For example, if you count 5, then 8, then 4 blue circles, you need to remember the sequence <code className="text-base font-mono bg-gray-100 px-1 rounded">5, 8, 4</code>.</p> <p><strong>4. Recall:</strong> After a few screens (a "set"), you'll see a keypad. Use it to enter the sequence of counts you remembered, in the correct order.</p> <p className={strongStyle}>Accuracy in both counting/judging <span className="text-red-600 font-bold">AND</span> recalling the counts in order is essential.</p> <p>Let's practice each part.</p> </div> <button onClick={handleNextOverallStep} className="px-5 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm shadow-sm transition-colors">Start Practice</button> </> );
        }

        // --- Render Processing Practice (Step 1) ---
        if (step === 'processingPractice' || step === 'processingPractice_done') {
            const totalTrials = PROCESSING_PRACTICE_EXAMPLES.length;
            return (
                 <>
                     <h3 className="text-lg font-semibold mb-3 text-gray-800">Practice 1: Counting & Judging</h3>
                      {step === 'processingPractice' && <p className="text-sm text-gray-600 mb-2">Focus only on counting <strong className={highlightStyle}>BLUE CIRCLES</strong> and judging Odd/Even. Don't worry about remembering the counts yet.</p>}
                      {step === 'processingPractice' && <p className="text-xs text-gray-500 mb-4">(Trial {trialNum + 1} of {totalTrials})</p>}
                      {step === 'processingPractice' && practiceSubStep === 'judgment' && currentStimulus?.items && (
                         <>
                             <StimulusDisplay items={currentStimulus.items} />
                             <p className="mt-3 mb-3 text-base font-medium text-gray-800">Is the count of <strong className={highlightStyle}>BLUE CIRCLES</strong> Odd or Even?</p>
                             <div className="flex space-x-4 justify-center">
                                 <button onClick={() => handlePracticeJudgment(true)} className="px-6 py-2 text-base font-semibold rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 shadow-sm">Odd</button>
                                 <button onClick={() => handlePracticeJudgment(false)} className="px-6 py-2 text-base font-semibold rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 shadow-sm">Even</button>
                             </div>
                         </>
                     )}
                     {feedback && ( <div className="mt-4 text-sm text-blue-700 bg-blue-50 p-3 rounded border border-blue-200 min-h-[60px] flex items-center justify-center">{feedback}</div> )}
                     {step === 'processingPractice_done' && (
                        <>
                           <p className="text-sm text-gray-600 my-4">Practice 1 Complete. Next, practice recalling sequences.</p>
                           <button onClick={handleNextOverallStep} className="px-5 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm shadow-sm transition-colors">Continue</button>
                        </>
                     )}
                 </>
            );
        }

        // --- Render Recall Practice (Step 2) ---
        if (step === 'recallPractice' || step === 'recallPractice_done') {
             const totalTrials = RECALL_PRACTICE_EXAMPLE_TRIALS.length;
            return (
                <>
                    <h3 className="text-lg font-semibold mb-3 text-gray-800">Practice 2: Recalling a Sequence</h3>
                    {step === 'recallPractice' && practiceSubStep === 'judgment' && <p className="text-sm text-gray-600 mb-2">Count the <strong className={highlightStyle}>BLUE CIRCLES</strong> and judge Odd/Even. Try to remember the sequence of counts.</p>}
                    {step === 'recallPractice' && practiceSubStep === 'judgment' && <p className="text-xs text-gray-500 mb-4">(Item {trialNum + 1} of {totalTrials})</p>}
                    {step === 'recallPractice' && practiceSubStep === 'recall' && <p className="text-sm text-gray-600 mb-2">Now, recall the <strong className={strongStyle}>{RECALL_PRACTICE_SET_SIZE}</strong> counts you just saw in the correct order.</p>}
                    {step === 'recallPractice' && practiceSubStep === 'judgment' && currentStimulus?.items && (
                         <>
                             <StimulusDisplay items={currentStimulus.items} />
                             <p className="mt-3 mb-3 text-base font-medium text-gray-800">Is the count Odd or Even?</p>
                             <div className="flex space-x-4 justify-center">
                                 <button onClick={() => handlePracticeJudgment(true)} className="px-6 py-2 text-base font-semibold rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 shadow-sm">Odd</button>
                                 <button onClick={() => handlePracticeJudgment(false)} className="px-6 py-2 text-base font-semibold rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 shadow-sm">Even</button>
                             </div>
                         </>
                    )}
                     {step === 'recallPractice' && practiceSubStep === 'recall' && (
                         <RecallInterface
                            setSize={RECALL_PRACTICE_SET_SIZE}
                            onSubmit={handleRecallPracticeSubmit}
                            value={practiceRecallSequence}
                            onChange={handlePracticeRecallChange}
                        />
                     )}
                     {feedback && ( <div className="mt-4 text-sm text-blue-700 bg-blue-50 p-3 rounded border border-blue-200 min-h-[60px] flex items-center justify-center">{feedback}</div> )}
                     {step === 'recallPractice_done' && (
                         <>
                             <p className="text-sm text-gray-600 my-4">Practice 2 Complete. Finally, let's combine everything.</p>
                             <button onClick={handleNextOverallStep} className="px-5 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm shadow-sm transition-colors">Continue</button>
                         </>
                     )}
                </>
            );
        }

         // --- Render Integrated Practice (Step 3) ---
        if (step === 'integratedPractice' || step === 'integratedPractice_done') {
             const totalTrials = INTEGRATED_PRACTICE_EXAMPLE_SET_TRIALS.length;
            return (
                <>
                    <h3 className="text-lg font-semibold mb-3 text-gray-800">Practice 3: Putting It Together</h3>
                    {step === 'integratedPractice' && <p className="text-sm text-gray-600 mb-2">Now, count, judge Odd/Even, <strong className={strongStyle}>AND remember</strong> the counts for recall.</p>}
                    {/* Using 1 for set number as only one set is defined */}
                    {step === 'integratedPractice' && <p className="text-xs text-gray-500 mb-4">(Practice Set 1, Trial {trialNum + 1} of {totalTrials})</p>}
                    {step === 'integratedPractice' && practiceSubStep === 'judgment' && currentStimulus?.items && (
                         <>
                             <StimulusDisplay items={currentStimulus.items} />
                             <p className="mt-3 mb-3 text-base font-medium text-gray-800">Is the count Odd or Even?</p>
                             <div className="flex space-x-4 justify-center">
                                 <button onClick={() => handlePracticeJudgment(true)} className="px-6 py-2 text-base font-semibold rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 shadow-sm">Odd</button>
                                 <button onClick={() => handlePracticeJudgment(false)} className="px-6 py-2 text-base font-semibold rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 shadow-sm">Even</button>
                             </div>
                         </>
                     )}
                     {step === 'integratedPractice' && practiceSubStep === 'recall' && (
                         <>
                            <p className="text-sm text-gray-600 mb-4">Recall the <strong className={strongStyle}>{INTEGRATED_PRACTICE_SET_SIZE}</strong> counts from this practice set in order.</p>
                            <RecallInterface
                                setSize={INTEGRATED_PRACTICE_SET_SIZE}
                                onSubmit={handleIntegratedRecallSubmit}
                                value={integratedRecallSequence}
                                onChange={handleIntegratedRecallChange}
                             />
                         </>
                     )}
                     {feedback && ( <div className={`mt-4 text-sm p-3 rounded border min-h-[60px] flex items-center justify-center ${step === 'integratedPractice_done' ? 'text-green-700 bg-green-50 border-green-200' : 'text-blue-700 bg-blue-50 border-blue-200'}`}>{feedback}</div> )}
                      {step === 'integratedPractice_done' && (
                         <>
                             <p className="text-sm text-gray-600 my-4">Practice Complete! Ready to start the main assessment?</p>
                             <button onClick={handleNextOverallStep} className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg shadow hover:bg-green-700 transition duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
                                  Start Assessment
                              </button>
                         </>
                     )}
                </>
            );
        }

        return <p>Loading Instructions...</p>;
    };

    return (
        <div className="text-center max-w-xl mx-auto p-4">
            {renderContent()}
        </div>
    );
};

CountingSpanInstructions.propTypes = {
    onStartAssessment: PropTypes.func.isRequired,
};

export default CountingSpanInstructions;